import React, { useEffect, useRef, useState } from 'react';
import './index.less';

/**
 * VisibilityObserver
 * Props:
 * - threshold: number (0-1), default 0.7
 * - effect: 'fade' | 'scale' | 'fade-up' | 'none' (default 'fade')
 * - duration: number (ms), default 400 - animation duration
 * - delay: number (ms), default 0 - animation delay
 * - onVisible: (entry: IntersectionObserverEntry) => void (optional)
 * - onEnded: () => void (optional) - callback when animation ends
 * - rootMargin: string (optional) - forward to IntersectionObserver
 * - className: string (optional)
 */
const VisibilityObserver = (props) => {
    const {
        threshold = 0.7,
        effect = 'fade',
        duration = 400,
        delay = 0,
        onVisible,
        onEnded,
        rootMargin,
        // whether the animation should only run once when first visible
        once = true,
        className = '',
        children,
    } = props;

    const containerRef = useRef(null);
    const observerRef = useRef(null);
    const hasShownRef = useRef(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        const handleIntersect = (entries) => {
            const entry = entries[0];

            // If we've already shown once and once=true, do nothing
            if (once && hasShownRef.current) {
                return;
            }

            if (entry && entry.intersectionRatio >= threshold) {
                setIsVisible(true);
                if (onVisible) onVisible(entry);

                if (once) {
                    hasShownRef.current = true;
                    if (observerRef.current) {
                        observerRef.current.disconnect();
                    }
                }
            } else {
                // Only toggle back to hidden when not running once
                if (!once) setIsVisible(false);
            }
        };

        observerRef.current = new IntersectionObserver(handleIntersect, {
            threshold: buildThresholdList(threshold),
            root: null,
            rootMargin,
        });

        observerRef.current.observe(containerRef.current);
        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [threshold, rootMargin, onVisible, once]);

    let effectClass = 'vo-fade';
    if (effect === 'scale') effectClass = 'vo-scale';
    else if (effect === 'fade-up') effectClass = 'vo-fade-up';
    else if (effect === 'none') effectClass = '';

    // 动态设置CSS变量
    const style = {
        '--vo-duration': `${duration}ms`,
        '--vo-delay': `${delay}ms`,
    };

    const handleTransitionEnd = (e) => {
        // 只在目标元素是容器本身且是 transform 或 opacity 属性的过渡结束时触发
        if (e.target === containerRef.current && 
            (e.propertyName === 'transform' || e.propertyName === 'opacity')) {
            // console.log('end');
            if (isVisible && onEnded) {
                onEnded();
            }
        }
    };

    return (
        <div 
            ref={containerRef} 
            className={`vo-container ${effectClass} ${isVisible ? 'is-visible' : ''} ${className}`}
            style={style}
            onTransitionEnd={handleTransitionEnd}
        >
            {children}
        </div>
    );
};

function buildThresholdList(th) {
    // Ensure we observe transitions around the chosen threshold for smoother effect
    const base = Math.max(0, Math.min(1, th));
    const steps = [0, 0.25, 0.5, base, 0.75, 1];
    return Array.from(new Set(steps)).sort();
}

export default VisibilityObserver;


